/**
 * 账单导出权限工具函数
 */

import { BillTypeEnum } from '../graphql/types/settlement/CommonTypes';

/**
 * 允许导出的账单状态列表
 */
const EXPORTABLE_BILL_STATUSES = [
  'INTERNAL_ALREADY',    // 账单已出账
  'ADJUSTMENT_PENDING',  // 账单调账待确认
  'INTERNAL_CONFIRMED',  // 账单已内部确认
  'EXTERNAL_REJECTED',   // 账单已申诉驳回
  'EXTERNAL_CONFIRMED',  // 账单已外部确认
  'LOCKED',              // 账单锁定
  'INVOICE_CONFIRMED',   // Invoice已确认（DSP账单）
  'PAID',                // 账单已付款（DSP账单）
];

/**
 * 允许调账的账单状态（与后端 SettlementBill.canAdjust 对齐）：
 * 账单已出账 / 调账待确认 / 申诉驳回待确认；待外部确认与外部已确认不可调账。
 */
const ADJUSTABLE_BILL_STATUSES = [
  'INTERNAL_ALREADY',
  'ADJUSTMENT_PENDING',
  'EXTERNAL_REJECTED',
];

/**
 * 检查账单是否允许导出
 *
 * @param bill 账单对象
 * @returns 是否允许导出
 */
export const canExportBill = (bill: { status: string; isCanceled?: boolean }): boolean => {
  if (bill.isCanceled) {
    return false;
  }
  return EXPORTABLE_BILL_STATUSES.includes(bill.status);
};

/**
 * 检查账单是否允许调账（明细调账按钮展示逻辑：与导出一致，状态满足时才出现）
 * 分拣账单不展示明细调账按钮（仅前端隐藏，后端调账接口不拦截）
 *
 * @param bill 账单对象
 * @returns 是否允许调账
 */
export const canAdjustBill = (bill: { status: string; isCanceled?: boolean; billType?: string }): boolean => {
  if (bill.isCanceled || bill.billType === BillTypeEnum.SORTING_BILL) {
    return false;
  }
  return ADJUSTABLE_BILL_STATUSES.includes(bill.status);
};

/**
 * 检查账单详情页是否展示 Invoice 按钮：已开票且非分拣账单
 *
 * @param bill 账单对象
 * @returns 是否展示 Invoice 按钮
 */
export const canViewInvoice = (bill: { isCanceled?: boolean; invoiceStatus?: string | null; billType?: string }): boolean => {
  if (bill.isCanceled || bill.billType === BillTypeEnum.SORTING_BILL) {
    return false;
  }
  return !!bill.invoiceStatus && bill.invoiceStatus !== 'NOT_ISSUED';
};

/**
 * 获取导出按钮的提示文本
 * 
 * @param bill 账单对象
 * @returns 提示文本
 */
export const getExportButtonTitle = (bill: { status: string; isCanceled?: boolean }): string => {
  if (bill.isCanceled) {
    return '账单已作废';
  }
  
  if (!EXPORTABLE_BILL_STATUSES.includes(bill.status)) {
    return '只有账单已出账及之后的状态才允许导出';
  }
  
  return '导出账单';
};

/**
 * 允许发送账单的账单状态（与后端 BillSendApplication 对齐）
 */
const SENDABLE_BILL_STATUSES = [
  'INTERNAL_CONFIRMED',
  'EXTERNAL_CONFIRMED',
];

/**
 * 检查账单是否允许发送
 */
export const canSendBill = (bill: { status: string; isCanceled?: boolean }): boolean => {
  if (bill.isCanceled) {
    return false;
  }
  return SENDABLE_BILL_STATUSES.includes(bill.status);
};

/**
 * 获取发送按钮提示文本
 */
export const getSendBillButtonTitle = (bill: { status: string; isCanceled?: boolean }): string => {
  if (bill.isCanceled) {
    return '账单已作废';
  }
  if (!SENDABLE_BILL_STATUSES.includes(bill.status)) {
    return '仅账单已内部确认或账单已外部确认状态允许发送';
  }
  return '发送账单';
};

/**
 * 获取账单状态的中文描述
 */
export const getBillStatusDescription = (status: string): string => {
  const statusMap: Record<string, string> = {
    'PRE_GENERATED': '账单初始化',
    'PENDING_SUMMARY': '账单汇总待出账',
    'INTERNAL_ALREADY': '账单已出账',
    'ADJUSTMENT_PENDING': '账单调账待确认',
    'INTERNAL_CONFIRMED': '账单已内部确认',
    'EXTERNAL_REJECTED': '账单已申诉驳回',
    'EXTERNAL_CONFIRMED': '账单已外部确认',
    'LOCKED': '账单锁定',
  };
  return statusMap[status] || '未知状态';
};

